import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Send, Bot, User, Hotel, MapPin, Globe, Phone, Mail,
  FileText, Star, Bed, Loader2, CheckCircle, Shield,
  Sparkles, ArrowRight, Coins, Building2, Eye,
  Camera, Upload, ImageIcon, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const FIELD_CONFIG = [
  { key: 'name', label: 'Hotel Name', icon: Hotel, placeholder: 'e.g. Villa Harmony Ubud' },
  { key: 'city', label: 'City', icon: MapPin, placeholder: 'e.g. Ubud' },
  { key: 'country', label: 'Country', icon: Globe, placeholder: 'e.g. Indonesia' },
  { key: 'address', label: 'Address', icon: Building2, placeholder: 'Full street address' },
  { key: 'phone', label: 'Phone', icon: Phone, placeholder: '+62 ...' },
  { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://...' },
  { key: 'email', label: 'Hotel Email', icon: Mail, placeholder: 'info@hotel.com' },
  { key: 'room_count', label: 'Rooms', icon: Bed, placeholder: 'Number of rooms' },
  { key: 'star_rating', label: 'Star Rating', icon: Star, placeholder: '1-5' },
  { key: 'hotel_type', label: 'Type', icon: Building2, placeholder: 'Boutique, Resort...' },
  { key: 'description', label: 'Description', icon: FileText, placeholder: 'Brief description' },
];

export default function HotelRegistrationPage() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [extracted, setExtracted] = useState({});
  const [completion, setCompletion] = useState(0);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [starting, setStarting] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Get owner email from localStorage
  const getOwnerEmail = () => {
    try {
      const user = JSON.parse(localStorage.getItem('easystay_user') || '{}');
      return user.email || 'unknown@getbitsy.ai';
    } catch { return 'unknown@getbitsy.ai'; }
  };

  // Start session
  useEffect(() => {
    const startSession = async () => {
      try {
        const res = await fetch(`${API}/api/registration/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ owner_email: getOwnerEmail() }),
        });
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages([{ role: 'assistant', content: data.response }]);
        if (data.extracted) setExtracted(data.extracted);
        if (data.completion_pct) setCompletion(data.completion_pct);
      } catch (err) {
        toast.error('Failed to start registration session');
      } finally {
        setStarting(false);
      }
    };
    startSession();
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || !sessionId || sending) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/api/registration/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: msg,
          owner_email: getOwnerEmail(),
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (data.extracted) setExtracted(data.extracted);
      if (data.completion_pct !== undefined) setCompletion(data.completion_pct);
      if (data.ready_to_submit) setReadyToSubmit(true);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [input, sessionId, sending]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);

    for (const file of files) {
      setMessages(prev => [...prev, {
        role: 'user',
        content: `Uploading photo: ${file.name}`,
        isPhoto: true,
        photoPreview: URL.createObjectURL(file),
      }]);

      try {
        const formData = new FormData();
        formData.append('file', file);
        // Use a temp hotel ID (session-based) until hotel is created
        const tempId = `pending_${sessionId}`;
        const res = await fetch(`${API}/api/photos/upload/${tempId}`, {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();

        if (data.success) {
          setPhotos(prev => [...prev, data]);
          const qualityMsg = data.quality_score >= 8 ? 'Excellent quality!' :
            data.quality_score >= 6 ? 'Good quality.' :
            data.quality_score >= 4 ? 'Acceptable, but could be better.' : 'This photo is low quality — consider retaking it.';

          // Build coverage info
          const updatedPhotos = [...photos, data];
          const covered = [...new Set(updatedPhotos.map(p => p.category))];
          const required = ['exterior', 'lobby', 'room'];
          const missing = required.filter(c => !covered.includes(c));
          const coverageNote = missing.length === 0
            ? `\n\nAll essential categories covered (${covered.join(', ')})!`
            : `\n\nStill need: ${missing.join(', ')}. ${updatedPhotos.length} photo${updatedPhotos.length > 1 ? 's' : ''} uploaded so far.`;

          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Photo analyzed! I've categorized this as **${data.category}** (${data.quality_score}/10). ${qualityMsg}\n\n${data.description}${data.enhancement_suggestion && data.enhancement_suggestion !== 'none' ? `\n\nTip: ${data.enhancement_suggestion}` : ''}${coverageNote}`,
            isPhotoResult: true,
            photoData: data,
          }]);
        } else {
          toast.error(data.detail || data.error || 'Upload failed');
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!sessionId) return;
    setSubmitting(true);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Submitting your hotel to HERMES Protocol for AI verification...',
      isSystem: true,
    }]);

    try {
      const res = await fetch(`${API}/api/registration/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();

      if (data.success) {
        const v = data.verification;
        setVerificationResult(v);
        const score = v?.confidence_score || 0;
        const decision = v?.decision || 'unknown';

        let resultMsg = '';
        if (decision === 'auto_approve') {
          resultMsg = `Your hotel "${data.hotel_name}" has been verified by HERMES AI (Score: ${score}/100)! Your HOTEL SBT has been minted and 100,000 HERMES tokens have been distributed to your hotel's treasury. Welcome to the protocol!`;
        } else if (decision === 'needs_review') {
          resultMsg = `Your hotel "${data.hotel_name}" scored ${score}/100 on HERMES verification. It's been flagged for admin review — our team will verify it within 24 hours. You'll receive full access once approved.`;
        } else if (decision === 'auto_reject') {
          resultMsg = `Your hotel "${data.hotel_name}" scored ${score}/100. The verification didn't pass this time. Reason: ${v?.analysis?.summary || 'Insufficient data'}. You can update your details and try again.`;
        } else {
          resultMsg = `Your hotel "${data.hotel_name}" has been submitted. Our team will review it shortly.`;
        }

        setMessages(prev => [...prev, { role: 'assistant', content: resultMsg, isResult: true, decision }]);
      } else {
        toast.error(data.error || 'Submission failed');
      }
    } catch (err) {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filledFields = FIELD_CONFIG.filter(f => extracted[f.key] && extracted[f.key] !== '').length;

  if (starting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="registration-loading">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Starting your registration agent...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="hotel-registration-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Eye className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <span className="font-semibold heading-font text-sm">Argus</span>
              <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">getbitsy.ai Registration Agent</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
              <span>{filledFields}/{FIELD_CONFIG.length} fields</span>
              <Progress value={completion} className="w-20 h-1.5" />
              <span>{completion}%</span>
            </div>
            {readyToSubmit && !verificationResult && (
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="submit-hotel-btn"
                className="animate-pulse"
              >
                {submitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Shield className="w-3 h-3 mr-1" />}
                Submit for Verification
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Split Panel */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* LEFT: Chat */}
        <div className="flex-1 lg:w-3/5 flex flex-col border-r border-border/30" data-testid="chat-panel">
          <ScrollArea className="flex-1 p-4">
            <div className="max-w-2xl mx-auto space-y-4 pb-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-7 h-7 rounded-lg shrink-0 flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-primary/10' : msg.isResult ? 'bg-amber-500/10' : 'bg-teal-500/10'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-3.5 h-3.5 text-primary" />
                    ) : msg.isResult ? (
                      <Shield className="w-3.5 h-3.5 text-amber-500" />
                    ) : (
                      <Eye className="w-3.5 h-3.5 text-teal-500" />
                    )}
                  </div>
                  <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : msg.isResult
                        ? msg.decision === 'auto_approve'
                          ? 'bg-emerald-500/10 border border-emerald-500/20 text-foreground'
                          : msg.decision === 'auto_reject'
                            ? 'bg-red-500/10 border border-red-500/20 text-foreground'
                            : 'bg-amber-500/10 border border-amber-500/20 text-foreground'
                        : msg.isPhotoResult
                          ? 'bg-violet-500/5 border border-violet-500/20 text-foreground'
                          : 'bg-muted/50 text-foreground'
                  }`} data-testid={`msg-${i}`}>
                    {msg.isPhoto && msg.photoPreview && (
                      <img src={msg.photoPreview} alt="upload" className="w-full max-w-[200px] rounded-lg mb-2" />
                    )}
                    {msg.isPhotoResult && msg.photoData && (
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 text-[10px]">
                          <Camera className="w-2.5 h-2.5 mr-1" />{msg.photoData.category}
                        </Badge>
                        <Badge className={`text-[10px] ${msg.photoData.quality_score >= 7 ? 'bg-emerald-500/10 text-emerald-400' : msg.photoData.quality_score >= 4 ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>
                          Quality: {msg.photoData.quality_score}/10
                        </Badge>
                      </div>
                    )}
                    {msg.content}
                    {msg.isResult && msg.decision === 'auto_approve' && (
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard">
                          <ArrowRight className="w-3 h-3 mr-1" /> Go to Dashboard
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-lg bg-teal-500/10 flex items-center justify-center">
                    <Eye className="w-3.5 h-3.5 text-teal-500" />
                  </div>
                  <div className="bg-muted/50 rounded-xl px-4 py-2.5">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                      <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                      <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          {!verificationResult && (
            <div className="border-t border-border/50 p-3 bg-card/40">
              <div className="max-w-2xl mx-auto flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                  data-testid="photo-file-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || submitting}
                  className="shrink-0"
                  data-testid="photo-upload-btn"
                  title="Upload hotel photos"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                </Button>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Chat with Argus..."
                  disabled={sending || submitting}
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending || submitting}
                  size="icon"
                  data-testid="chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Live Form */}
        <div className="lg:w-2/5 bg-card/20 overflow-auto" data-testid="form-panel">
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold heading-font">Hotel Details</h2>
              <Badge variant={completion >= 80 ? 'default' : 'secondary'} className="text-[10px]">
                {completion}% complete
              </Badge>
            </div>
            <Progress value={completion} className="h-1.5" data-testid="form-progress" />

            <div className="space-y-2">
              {FIELD_CONFIG.map(({ key, label, icon: Icon, placeholder }) => {
                const value = extracted[key] || '';
                const filled = value !== '';
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-all duration-300 ${
                      filled
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-border/40 bg-background'
                    }`}
                    data-testid={`field-${key}`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                      filled ? 'bg-primary/10' : 'bg-muted'
                    }`}>
                      {filled ? (
                        <CheckCircle className="w-3.5 h-3.5 text-primary" />
                      ) : (
                        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
                      <p className={`text-sm truncate ${filled ? 'text-foreground font-medium' : 'text-muted-foreground/40 italic'}`}>
                        {filled ? value : placeholder}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Photo Gallery */}
            {photos.length > 0 && (
              <div className="mt-4" data-testid="photo-gallery">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold heading-font flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5 text-primary" /> Photos ({photos.length})
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Add More
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden border border-border/40 bg-muted/20" data-testid={`photo-thumb-${i}`}>
                      <div className="aspect-[3/2] flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-muted-foreground/30" />
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                        <Badge className="text-[8px] bg-white/20 text-white border-none">{p.category}</Badge>
                        <div className={`absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${
                          p.quality_score >= 7 ? 'bg-emerald-500 text-white' : p.quality_score >= 4 ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'
                        }`}>{p.quality_score}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Upload Prompt (when no photos yet) */}
            {photos.length === 0 && !verificationResult && (
              <div
                className="mt-4 border-2 border-dashed border-border/40 rounded-xl p-4 text-center cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="photo-drop-zone"
              >
                <Camera className="w-6 h-6 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Click to upload hotel photos</p>
                <p className="text-[10px] text-muted-foreground/60 mt-1">Argus will auto-categorize and optimize them</p>
              </div>
            )}

            {/* Submit CTA */}
            {readyToSubmit && !verificationResult && (
              <Card className="border-2 border-primary/40 shadow-lg shadow-primary/5 mt-4" data-testid="submit-card">
                <CardContent className="p-4 text-center space-y-3">
                  <Sparkles className="w-6 h-6 text-primary mx-auto" />
                  <p className="text-sm font-medium heading-font">Ready to submit!</p>
                  <p className="text-xs text-muted-foreground">
                    Argus will hand off to Hermes Agent for verification.
                  </p>
                  <Button onClick={handleSubmit} disabled={submitting} className="w-full" data-testid="form-submit-btn">
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Hermes Agent verifying...</>
                    ) : (
                      <><Shield className="w-4 h-4 mr-2" /> Submit to Hermes Agent</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Verification Result */}
            {verificationResult && (
              <Card className={`border-2 mt-4 ${
                verificationResult.decision === 'auto_approve'
                  ? 'border-emerald-500/40'
                  : verificationResult.decision === 'auto_reject'
                    ? 'border-red-500/40'
                    : 'border-amber-500/40'
              }`} data-testid="verification-result">
                <CardContent className="p-4 space-y-3">
                  <div className="text-center">
                    <div className={`w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center ${
                      verificationResult.decision === 'auto_approve' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                    }`}>
                      <Shield className={`w-6 h-6 ${
                        verificationResult.decision === 'auto_approve' ? 'text-emerald-500' : 'text-amber-500'
                      }`} />
                    </div>
                    <p className="font-semibold heading-font text-sm">
                      HERMES Score: {verificationResult.confidence_score}/100
                    </p>
                    <Badge className={`text-[10px] mt-1 ${
                      verificationResult.decision === 'auto_approve'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : verificationResult.decision === 'auto_reject'
                          ? 'bg-red-500/10 text-red-600'
                          : 'bg-amber-500/10 text-amber-600'
                    }`}>
                      {verificationResult.decision === 'auto_approve' ? 'Verified' :
                       verificationResult.decision === 'needs_review' ? 'Under Review' : 'Needs More Info'}
                    </Badge>
                  </div>

                  {verificationResult.decision === 'auto_approve' && (
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center gap-2 text-xs text-emerald-600">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>HOTEL SBT Minted</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-emerald-600">
                        <Coins className="w-3.5 h-3.5" />
                        <span>100,000 HERMES Distributed</span>
                      </div>
                      <Button onClick={() => navigate('/dashboard')} className="w-full mt-2" size="sm" data-testid="result-dashboard-btn">
                        <ArrowRight className="w-3 h-3 mr-1" /> Go to Dashboard
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Info footer */}
            <Separator />
            <p className="text-[10px] text-muted-foreground text-center">
              Argus (getbitsy.ai) + Hermes Agent (HERMES Protocol)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
